import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Fade,
  Grid,
  SxProps,
  Tab,
  Tabs,
} from '@mui/material';

import { getCoursesState } from '../../../../../../store/V2/CoursesSlice';
import { TP_COLORS } from '../../../../../../constants';
import { useAppSelector } from '../../../../../../hooks';
import BasicDetailsTab from './BasicDetailsTab';
import TutorsTab from './TutorsTab';
import StudentsTab from './StudentsTab';
import PricingTab from './PricingTab';
import OthersTab from './Others';

interface CourseOptionsModalProps {
  open: boolean;
  onClose: () => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
  style?: React.CSSProperties;
}

export function TabPanel(props: TabPanelProps): JSX.Element {
  const { children, value, index, ...other } = props;

  return (
    <Fade in>
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`vertical-tabpanel-${index}`}
        aria-labelledby={`vertical-tab-${index}`}
        {...other}
      >
        {value === index && children}
      </div>
    </Fade>
  );
}

const a11yProps = (index: number) => {
  return {
    id: `vertical-tab-${index}`,
    'aria-controls': `vertical-tabpanel-${index}`,
  };
};

const tabStyles: SxProps = {
  borderRight: 1,
  borderColor: 'divider',
  position: 'sticky',
  top: '0px',
};

const tabLiStyles: SxProps = {
  textAlign: 'unset',
  alignItems: 'flex-start',
  fontWeight: 600,
  color: TP_COLORS.black,
  '&.Mui-selected': {
    color: TP_COLORS.black,
  },
};

const tabs = [
  {
    index: 0,
    title: 'Basic Details',
  },
  {
    index: 1,
    title: 'Pricing',
  },
  {
    index: 2,
    title: 'Tutors',
  },
  {
    index: 3,
    title: 'Students',
  },
  {
    index: 4,
    title: 'Others',
  },
];

const CourseOptionsModal: React.FC<CourseOptionsModalProps> = ({
  open,
  onClose,
}) => {
  // State
  const [selectedTabValue, setSelectedTab] = useState(0);

  // Store
  const coursesState = useAppSelector(getCoursesState);

  const courseData = coursesState.adminCourse;

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  const renderTabPanes = () => {
    const _panelProps = {
      selectedTabValue,
      selectedTabIndex: selectedTabValue,
    };

    if (selectedTabValue === 0) {
      return <BasicDetailsTab {..._panelProps} />;
    }

    if (selectedTabValue === 1) {
      return <PricingTab {..._panelProps} />;
    }

    if (selectedTabValue === 2) {
      return <TutorsTab {..._panelProps} />;
    }

    if (selectedTabValue === 3) {
      return <StudentsTab {..._panelProps} />;
    }

    if (selectedTabValue === 4) {
      return <OthersTab {..._panelProps} />;
    }

    return null;
  };

  return (
    <Dialog
      fullWidth
      maxWidth="lg"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { minHeight: '600px' } }}
    >
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4} md={3} lg={2}>
            <Tabs
              orientation="vertical"
              variant="scrollable"
              aria-label="Vertical tabs example"
              TabIndicatorProps={{ hidden: true }}
              value={selectedTabValue}
              onChange={handleTabChange}
              sx={tabStyles}
            >
              {tabs.map((item) => {
                const isSelected = item.index === selectedTabValue;

                let isDisabled = false;

                if (item.index === 3) {
                  isDisabled = true;

                  if (courseData) {
                    if (courseData.groupClassId || courseData.isOneToOne) {
                      isDisabled = false;
                    }
                  }
                }

                return (
                  <Tab
                    {...a11yProps(item.index)}
                    key={`tab-li-${item.index}`}
                    label={item.title}
                    disabled={isDisabled}
                    sx={{
                      ...tabLiStyles,
                      backgroundColor: isSelected
                        ? 'rgba(0,0,0,0.05)'
                        : undefined,
                    }}
                  />
                );
              })}
            </Tabs>
          </Grid>
          <Grid item xs={12} sm={8} md={9} lg={10}>
            {renderTabPanes()}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: '16px' }}>
        <Button
          size="small"
          variant="contained"
          color="inherit"
          onClick={onClose}
        >
          Dismiss
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CourseOptionsModal;
